/**
 * Owns the Notification Center's in-memory Redux state, authentication readiness, and local ingestion.
 * Notification state is intentionally not persisted through notification CRUD APIs.
 */
import {createSlice, type PayloadAction, createAsyncThunk} from '@reduxjs/toolkit';
import {
  QEPNotificationStatus, QEPNotificationType, type QEPNotification, type QEPNotificationActionType, type QEPNotificationPayload,
} from '@qep/types/src';
import {getContainer} from '../../../utils/utils.js';
import {processNotification} from './notification-display-rules.js';

type NotificationsState = {
  notifications: Array<QEPNotification<QEPNotificationActionType>>;
  user: Record<string, unknown> | undefined;
  loaded: boolean;
  flashingNotification: QEPNotification<QEPNotificationActionType> | undefined;
};

const initialState: NotificationsState = {
  notifications: [],
  user: undefined,
  loaded: false,
  flashingNotification: undefined,
};

const getAuthInfo = async () => {
  const browser = getContainer();
  const authInfo = await browser.applicationClient.getAuthInfo();
  if (authInfo) {
    return authInfo;
  }

  return new Promise(resolve => {
    const onAuthChange = async (message: {message: any}) => {
      if (message.message) {
        const authInfo = await browser.applicationClient.getAuthInfo();
        if (!authInfo) {
          return;
        }

        resolve(authInfo);
        browser.applicationClient.removeListener(browser.applicationClient.APPLICATION_EVENTS.AUTH.AUTH_STATUS_CHANGE.topic, onAuthChange);
      }
    };

    browser.applicationClient.addListener(browser.applicationClient.APPLICATION_EVENTS.AUTH.AUTH_STATUS_CHANGE.topic, onAuthChange);
  });
};

export const loadNotifications = createAsyncThunk(
  'notifications/loadNotifications',
  async (_, {rejectWithValue}) => {
    try {
      const authInfo = await getAuthInfo() as Record<string, unknown>;
      // You can fetch notifications here if needed, e.g.:
      // const notifications = await fetchNotifications(authInfo);
      return {user: authInfo /* , notifications */};
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

/** Processes an incoming notification against current state and stores the result locally. */
export const addIncomingNotification = createAsyncThunk(
  'notifications/addIncomingNotification',
  async (notification: QEPNotificationPayload<QEPNotificationActionType>, {dispatch, getState, rejectWithValue}) => {
    try {
      const state = getState() as {notifications: {notifications: Array<QEPNotification<QEPNotificationActionType>>}};
      const existing = state.notifications.notifications.find(item => item.id === notification.id);
      const processed = processNotification(notification, existing);
      dispatch(addNotification(processed));
      return processed;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setNotifications(state, action: PayloadAction<Array<QEPNotification<QEPNotificationActionType>>>) {
      state.notifications = action.payload;
      // Determine flashing notification
      const unreadNotifications = action.payload.filter(n => !n.isRead);
      // Find newest unread CRITICAL or SYSTEM notification
      const criticalOrSystem = unreadNotifications
        .filter(n => n.status === QEPNotificationStatus.CRITICAL || n.type === QEPNotificationType.SYSTEM)
        .sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
      if (criticalOrSystem.length > 0) {
        state.flashingNotification = criticalOrSystem[0];
      } else if (unreadNotifications.length > 0) {
        // Fallback to newest unread notification
        const sorted = unreadNotifications.sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
        state.flashingNotification = sorted[0];
      } else {
        state.flashingNotification = undefined;
      }
    },
    setFlashingNotification(state, action: PayloadAction<QEPNotification<QEPNotificationActionType> | undefined>) {
      state.flashingNotification = action.payload;
    },
    addNotification(state, action: PayloadAction<QEPNotification<QEPNotificationActionType>>) {
      const existingIndex = state.notifications.findIndex(n => n.id === action.payload.id);
      if (existingIndex === -1) {
        state.notifications.push(action.payload);
      } else {
        state.notifications[existingIndex] = action.payload;
      }

      state.notifications.sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));

      // Update flashing notification
      const unreadNotifications = state.notifications.filter(n => !n.isRead);
      const criticalOrSystem = unreadNotifications
        .filter(n => n.status === QEPNotificationStatus.CRITICAL || n.type === QEPNotificationType.SYSTEM)
        .sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
      if (criticalOrSystem.length > 0) {
        state.flashingNotification = criticalOrSystem[0];
      } else if (unreadNotifications.length > 0) {
        const sorted = unreadNotifications.sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
        state.flashingNotification = sorted[0];
      } else {
        state.flashingNotification = undefined;
      }
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
      // Update flashing notification
      const unreadNotifications = state.notifications.filter(n => !n.isRead);
      const criticalOrSystem = unreadNotifications
        .filter(n => n.status === QEPNotificationStatus.CRITICAL || n.type === QEPNotificationType.SYSTEM)
        .sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
      if (criticalOrSystem.length > 0) {
        state.flashingNotification = criticalOrSystem[0];
      } else if (unreadNotifications.length > 0) {
        const sorted = unreadNotifications.sort((a, b) => (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
        state.flashingNotification = sorted[0];
      } else {
        state.flashingNotification = undefined;
      }
    },
    clearNotifications(state) {
      state.notifications = [];
      state.flashingNotification = undefined;
    },
    /** Marks every locally stored notification as read and clears the flashing indicator. */
    markAllAsRead(state) {
      state.notifications = state.notifications.map(n => ({...n, isRead: true}));
      state.flashingNotification = undefined;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadNotifications.pending, state => {
        state.loaded = false;
      })
      .addCase(loadNotifications.fulfilled, (state, action) => {
        state.user = action.payload.user;
        // state.notifications = action.payload.notifications || [];
        state.loaded = true;
      })
      .addCase(loadNotifications.rejected, state => {
        state.loaded = false;
      });
  },
});

export const {
  setNotifications,
  addNotification,
  removeNotification,
  clearNotifications,
  markAllAsRead,
  setFlashingNotification,
} = notificationsSlice.actions;

export const selectFlashingNotification = (state: {notifications: NotificationsState}) => state.notifications.flashingNotification;

export default notificationsSlice.reducer;
